import React, { useEffect, useState } from 'react';
import Box from 'ui/Box';
import styled, { css } from 'styled-components';
import Body from 'ui/Typography/Body';
import ErrorIcon from 'ui/icons/error.svg?react';
import uiTheme from 'ui/theme';
import { SpaceProps } from 'styled-system';
import { useServiceSettings } from 'contexts/serviceSettings';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KeyboardManager } from '@inter-ikea-kompis/keyboard-manager';
import { usesVirtualKeyboard } from 'utils/platform';

const templateKeyValue = '{value}';
const templateKeyUnit = '{unit}';

const transition = (property = 'all') => `transition: ${property} 0.16s ease-in-out;`;

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const TitleAndInput = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Flex = styled.div`
  display: flex;
`;

const TextInput = styled.input<{ fontFamily: string }>`
  width: ${({ theme }) => theme.space.xxl};
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[100]};
  border-radius: ${({ theme }) => theme.space.xxxs};
  font-family: ${({ fontFamily }) => fontFamily};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.feedback.focus};
  }

  &[data-error='true'] {
    outline: 2px solid ${({ theme }) => theme.colors.feedback.error};
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${({ theme }) => theme.space.l};
`;

type LineProps = {
  fill: number;
};

const thumbStyle = css`
  appearance: none;
  width: ${({ theme }) => `calc(${theme.space.l} + 1px)`};
  height: ${({ theme }) => `calc(${theme.space.l} + 1px)`};
  background: ${({ theme }) => theme.colors.grey[900]};
  border-radius: 50%;
  margin-top: -11px;
  border: none;
  ${transition()}
  opacity: 1;
`;

const trackStyle = css`
  height: 3px;
  background: ${({ theme }) => theme.colors.grey[200]};
`;

const FilledLine = styled.div.attrs<LineProps>((props: LineProps): any => ({
  style: {
    width: `${props.fill}%`
  }
}))`
  position: absolute;
  top: 12px;
  background: ${({ theme }) => theme.colors.grey[900]};
  height: 3px;
  z-index: 1;
  pointer-events: none;
  ${transition('opacity')}
  opacity: 1;
  &[data-hide='true'] {
    opacity: 0;
  }
`;

const SliderInput = styled.input`
  width: 100%;
  appearance: none;
  margin: 0;
  height: 3px;
  &::-moz-range-track {
    ${trackStyle}
  }
  &::-webkit-slider-runnable-track {
    ${trackStyle}
  }
  &::-webkit-slider-thumb {
    ${thumbStyle}
  }
  &::-moz-range-thumb {
    ${thumbStyle}
  }

  &[data-hide-thumb='true'] {
    &::-webkit-slider-thumb {
      ${thumbStyle}
      opacity: 0;
    }
    &::-moz-range-thumb {
      ${thumbStyle}
      opacity: 0;
    }
  }
`;

const Bounds = styled.div`
  height: ${({ theme }) => theme.space.l};
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  ${transition()}
  opacity: 1;
  transform: translate(0, 0);
  &[data-state='hidden'] {
    opacity: 0;
    transform: translate(0, -${({ theme }) => theme.space.l});
  }
  &[data-state='obscured'] {
    opacity: 0;
  }
`;

const ErrorMessage = styled.div`
  height: ${({ theme }) => theme.space.l};
  margin-top: -${({ theme }) => theme.space.l};
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  ${transition()}
  opacity: 1;
  &[data-hide='true'] {
    opacity: 0;
  }
`;

type Props = SpaceProps & {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  onChangeEnd?: () => void;
  strings: {
    label: string;
    min: string;
    max: string;
    unit: string;
    errorBelowMin: string;
    errorAboveMax: string;
  };
};

const Slider = ({ strings, min, max, value, onChange, onChangeEnd, ...props }: Props) => {
  const [textInputValue, setTextInputValue] = useState<number>(value);
  const [textInputFocused, setTextInputFocused] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const keyboardManager = new KeyboardManager();

  const { theme } = useServiceSettings();

  useEffect(() => {
    if (textInputValue !== value && !textInputFocused) {
      setTextInputValue(value);
    }
  }, [value, textInputValue, textInputFocused]);

  const fill = isNaN(value) ? 0 : Math.min(((value - min) / (max - min)) * 100, 100);

  const onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const desiredValue = parseInt(event.target.value);
    const clampedValue = Math.min(max, Math.max(min, desiredValue));
    setTextInputValue(desiredValue);

    setErrorMessage(
      desiredValue > max
        ? strings.errorAboveMax
            .replace(templateKeyValue, max + '')
            .replace(templateKeyUnit, strings.unit)
        : desiredValue < min
        ? strings.errorBelowMin
            .replace(templateKeyValue, min + '')
            .replace(templateKeyUnit, strings.unit)
        : null
    );

    onChange(clampedValue);
  };

  const onTextInputFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    usesVirtualKeyboard() && keyboardManager.setFocus(event.target);
    setTextInputFocused(true);
  };

  const onTextInputBlur = () => {
    setTextInputFocused(false);
    usesVirtualKeyboard() && keyboardManager.removeFocus();
    setTextInputValue(value);
    setErrorMessage(null);
    onChangeEnd && onChangeEnd();
  };

  const onTextInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    setTextInputValue(value);
    setErrorMessage(null);
    event.currentTarget.focus();
    event.currentTarget.select();
  };

  const onSliderInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    setTextInputValue(value);
    onChange(value);
  };

  return (
    <Wrapper {...props}>
      <TitleAndInput>
        <Body
          id={`slider-label-${strings.label}`}
          as="label"
          fontWeight="bold"
          type={ThemeFontStyleTypeEnum.bodyMedium}
        >
          {strings.label}
        </Body>
        <Flex>
          <TextInput
            data-testid="slider-input-field"
            aria-labelledby={`slider-label-${strings.label}`}
            data-error={!!errorMessage}
            value={textInputValue === undefined || isNaN(textInputValue) ? '' : textInputValue}
            onChange={onTextInputChange}
            onFocus={onTextInputFocus}
            onBlur={onTextInputBlur}
            onKeyPress={onTextInputKeyPress}
            onInput={onTextInputChange}
            fontFamily={`${theme.fontFace.family}, ${theme.fontFace.fallbacks.join(',')}`}
            inputMode="numeric"
          />
          <Body fontWeight="bold" ml="s" type={ThemeFontStyleTypeEnum.bodySmall}>
            {strings.unit}
          </Body>
        </Flex>
      </TitleAndInput>
      <Box mt="md">
        <SliderWrapper>
          <FilledLine fill={fill} data-hide={textInputFocused} />
          <SliderInput
            type="range"
            role="slider"
            min={min}
            max={max}
            step="1"
            value={isNaN(value) ? 0 : value}
            onChange={onSliderInputChange}
            onMouseUp={onChangeEnd}
            aria-labelledby={`slider-label-${strings.label}`}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={isNaN(value) ? 0 : value}
            data-hide-thumb={textInputFocused}
          />
        </SliderWrapper>
        <Bounds data-state={errorMessage ? 'obscured' : textInputFocused ? 'normal' : 'hidden'}>
          <Body
            fontWeight="bold"
            type={ThemeFontStyleTypeEnum.bodySmall}
            color="feedback.information"
          >
            {strings.min.replace(templateKeyValue, min + '')}
          </Body>
          <Body
            fontWeight="bold"
            type={ThemeFontStyleTypeEnum.bodySmall}
            color="feedback.information"
          >
            {strings.max.replace(templateKeyValue, max + '')}
          </Body>
        </Bounds>
        <ErrorMessage data-hide={!errorMessage}>
          <ErrorIcon width="16" height="16" />
          <Body ml="xs" type={ThemeFontStyleTypeEnum.bodySmall} color={uiTheme.colors.grey[700]}>
            {errorMessage}
          </Body>
        </ErrorMessage>
      </Box>
    </Wrapper>
  );
};

export default Slider;
